import { useEffect, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Select from 'components/shared/Select'
import InlineTextList from 'components/shared/InlineTextList'
import { OptionProps } from 'components/shared/Select/index'
import { useListTransportDeparturePlacesQuery } from 'store/api-slice/hooks'
import {
    TransportRouteType,
    TransportDepartureType,
} from 'store/api-slice/types'
import { formatPrice } from 'helpers/helpers'
import Loader from 'components/shared/Loader'
import { useApp } from 'store/steps'

interface BusDestinationProps {
    routeId: number | string
    startCity: TransportDepartureType
    setStartCity: (startCity: TransportDepartureType) => void
    routeOption: TransportRouteType
    duration: string
    unitPrice: number | null
    setIsDataFetched: (isFetched: boolean) => void
    testIdPrefix?: string
}

export const BusDestination = ({
    routeId,
    startCity,
    setStartCity,
    routeOption,
    duration,
    unitPrice,
    setIsDataFetched,
    testIdPrefix,
}: BusDestinationProps) => {
    const { formatMessage } = useIntl()
    const { language } = useApp()

    const [currentStartPlaces, setCurrentStartPlaces] = useState<OptionProps[]>(
        [{ option: '', value: '', optionInfo: '' }]
    )

    const { data, isFetching, isError } = useListTransportDeparturePlacesQuery({
        _locale: language,
    })

    const isDataFetched = !isFetching && !isError

    const directionHeader = defineMessages({
        to_from_park: {
            id: 'upsells.transport.departure_label_to_from_park',
            defaultMessage: 'Gdzie zaczynasz podróż?',
        },
        to_park: {
            id: 'upsells.transport.departure_label_to_park',
            defaultMessage: 'Skąd chcesz jechać?',
        },
        from_park: {
            id: 'upsells.transport.departure_label_from_park',
            defaultMessage: 'Dokąd chcesz jechać?',
        },
    })

    const formatOptions = () => {
        if (!data) return
        const placesArr: OptionProps[] = []
        const { places } = data

        places?.forEach((place) => {
            const { label, value, description } = place
            if (!label) return
            placesArr.push({
                option: `${label}, ${description}`,
                value,
                optionInfo: '',
            })
        })

        return setCurrentStartPlaces(placesArr)
    }

    const handleChangeDirection = (value: string) => {
        if (value !== startCity) setStartCity(value as TransportDepartureType)
    }

    useEffect(() => {
        setIsDataFetched(isDataFetched)
        if (!isDataFetched || !data) return
        formatOptions()
    }, [isDataFetched])

    return (
        <Box
            gap={8}
            isFullWidth
            id={`transport-ticket-${routeId}-routeDirection`}
        >
            <Text variant="heading4">
                {formatMessage(directionHeader[routeOption])}
            </Text>

            {!isDataFetched ? (
                <Loader />
            ) : (
                <Select
                    id="busStart"
                    options={currentStartPlaces}
                    value={startCity}
                    onChange={handleChangeDirection}
                    testId={`${
                        testIdPrefix ?? `transport-${routeId}`
                    }-direction-select`}
                />
            )}

            {!!duration && !!unitPrice && (
                <Box direction="row" gap={4} align="center" wrap="wrap">
                    <InlineTextList
                        items={[
                            duration,
                            `${formatPrice(
                                routeOption === 'to_from_park'
                                    ? unitPrice / 2
                                    : unitPrice,
                                true
                            )}${formatMessage({
                                id: 'global.price.label_single_person',
                                defaultMessage: '/os.',
                            })}`,
                        ]}
                        variant="body"
                        color="grayscale_900"
                        dotColor="grayscale_700"
                    />
                    <Text variant="body" color="grayscale_700">
                        {formatMessage({
                            id: 'upsells.transport.one_direction_label',
                            defaultMessage: 'w jedną stronę',
                        })}
                    </Text>
                </Box>
            )}
        </Box>
    )
}
